/**
 * Sample Excel templates for the Upload page.
 * Column names match exactly what excelParser.js reads, so a filled-in
 * sample uploads without any renaming.
 */
import * as XLSX from 'xlsx';

// ── Input Data ──────────────────────────────────────────────────────
const INPUT_COLUMNS = [
    { name: 'Part Number', required: 'Recommended', width: 16, note: 'Unique ID for the record. Shown on the Results page and used to match rows on export/import.' },
    { name: 'Description 1', required: 'Required*', width: 40, note: 'Primary item description.' },
    { name: 'Description 2', required: 'Optional', width: 40, note: 'Additional description / long text. Combined with Description 1.' },
    { name: 'given_Supplier Name', required: 'Optional', width: 30, note: 'Supplier or vendor name. Used to prioritise vendor URLs.' },
    { name: 'given_Supplier Partnumber', required: 'Required*', width: 24, note: 'Supplier / manufacturer part number used for search and DB lookup.' },
];

const INPUT_SAMPLE_ROWS = [
    ['PN-1001', 'BEARING 608-2Z SKF', '608-2Z DOUBLE SHIELDED DEEP GROOVE BALL BEARING', 'APPLIED INDUSTRIAL TECHNOLOGIES INC', '608-2Z'],
    ['PN-1002', 'BROACH;CUTR,22X50 MM', '', '', 'TK00731-0'],
    ['PN-1003', 'BEARING, PILLOW BLOCK, 1 IN BORE, CAST IRON HOUSING', '', '', ''],
];

// ── INC Data ────────────────────────────────────────────────────────
const INC_COLUMNS = [
    { name: 'ESCN', required: 'Required', width: 26, note: 'Noun/Modifier name. Must match the Noun/Modifier resolved for the input rows exactly (case-insensitive).' },
    { name: 'SEQ', required: 'Required', width: 8, note: 'Order of the property in the output (10, 20, 30 …).' },
    { name: 'Property', required: 'Required', width: 26, note: 'Property name to extract for this Noun/Modifier.' },
];

const INC_PROPERTIES = ['TYPE', 'SIZE', 'MATERIAL', 'FINISH/COLOR', 'RATING', 'STANDARD', 'APPLICATION'];
const INC_SAMPLE_ROWS = ['BEARING: BALL', 'BROACH', 'BEARING: PILLOW BLOCK'].flatMap(escn =>
    INC_PROPERTIES.map((property, i) => [escn, (i + 1) * 10, property])
);

// ── Helpers ─────────────────────────────────────────────────────────
function buildDataSheet(columns, rows) {
    const ws = XLSX.utils.aoa_to_sheet([columns.map(c => c.name), ...rows]);
    ws['!cols'] = columns.map(c => ({ wch: c.width }));
    ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length, c: columns.length - 1 } }) };
    return ws;
}

function buildInstructionsSheet(columns, footer) {
    const ws = XLSX.utils.aoa_to_sheet([
        ['Column', 'Required', 'Description'],
        ...columns.map(c => [c.name, c.required, c.note]),
        [],
        ...footer.map(line => [line]),
    ]);
    ws['!cols'] = [{ wch: 28 }, { wch: 14 }, { wch: 100 }];
    return ws;
}

function downloadWorkbook(wb, filename) {
    XLSX.writeFile(wb, filename, { compression: true });
}

// ── Public API ──────────────────────────────────────────────────────
export function downloadInputSample() {
    const wb = XLSX.utils.book_new();
    // Data sheet first: the parser picks the sheet whose headers best match known columns.
    XLSX.utils.book_append_sheet(wb, buildDataSheet(INPUT_COLUMNS, INPUT_SAMPLE_ROWS), 'Input Data');
    XLSX.utils.book_append_sheet(wb, buildInstructionsSheet(INPUT_COLUMNS, [
        '* Each row needs at least a Description or a Part Number (given_Supplier Partnumber) to be processed.',
        'Keep the column headers exactly as shown. Replace the sample rows with your own data.',
        'Part Number should be unique — duplicates are flagged in the run log.',
    ]), 'Instructions');
    downloadWorkbook(wb, 'Sample Input Data.xlsx');
}

export function downloadIncSample() {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, buildDataSheet(INC_COLUMNS, INC_SAMPLE_ROWS), 'INC');
    XLSX.utils.book_append_sheet(wb, buildInstructionsSheet(INC_COLUMNS, [
        'Add one row per property for each Noun/Modifier (ESCN). Properties are extracted in SEQ order.',
        'Only the first sheet is read — keep the INC data on the first sheet.',
        'Keep the column headers exactly as shown. Replace the sample rows with your own data.',
    ]), 'Instructions');
    downloadWorkbook(wb, 'Sample INC Data.xlsx');
}
